/**
 * @fileoverview Servicio de Formularios Públicos de Eventos
 *
 * Este módulo proporciona servicios para la gestión de formularios públicos
 * de registro de eventos, permitiendo obtener información y registrar usuarios
 * sin necesidad de autenticación.
 *
 * @author Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 * @date [Fecha de creación: 2025-11-12]
 * @updated [Fecha de última modificación: 2025-11-12]
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */

/**
 * Obtiene información pública de un formulario de registro usando su slug.
 *
 * Este método realiza una petición GET al endpoint público para obtener
 * los datos del formulario de registro de un evento, incluyendo campos
 * personalizados, fechas de vigencia y disponibilidad.
 *
 * @param {string} slug - Identificador único del formulario en formato URL-friendly.
 * @throws {Error} Si ocurre un error en la comunicación con el servidor.
 * @returns {Promise<Object>} Objeto con status y data del formulario público.
 */
export const getPublicFormInfo = async (slug) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/public/register/${slug}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const result = await response.json();
    console.log("Respuesta pública del formulario:", result);

    // El backend devuelve success y payload
    if (response.ok && (result.status === true || result.success === true)) {
      const data = result.data || result.payload;
      return { status: true, data: data };
    } else {
      const message = result.message || "Error al obtener información del formulario";
      return { status: false, message };
    }
  } catch (error) {
    console.error("Error en getPublicFormInfo:", error);
    const message = error.message || "Error al obtener información del formulario";
    return { status: false, message };
  }
};

/**
 * Registra a un usuario en un evento mediante API pública.
 *
 * Este método envía los datos del usuario al endpoint público de registro,
 * validando la información y creando el registro en el sistema sin necesidad
 * de autenticación previa.
 *
 * @param {string} slug - Identificador único del formulario en formato URL-friendly.
 * @param {Object} userData - Datos del usuario a registrar.
 * @param {string} userData.name - Nombre del usuario.
 * @param {string} userData.lastname - Apellido del usuario.
 * @param {string} userData.number_id - Documento de identidad del usuario.
 * @param {string} userData.email - Correo electrónico del usuario.
 * @param {string} [userData.blood_type] - Tipo de sangre del usuario (opcional).
 * @param {Object} [userData.custom_data] - Datos personalizados adicionales (opcional).
 * @throws {Error} Si ocurre un error en la comunicación con el servidor.
 * @returns {Promise<Object>} Objeto con status y data del registro creado.
 */
export const registerToEvent = async (slug, userData) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/public/register/${slug}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });

    const result = await response.json();
    console.log("Respuesta del registro:", result);

    // El backend devuelve success y payload
    if (response.ok && (result.status === true || result.success === true)) {
      const data = result.data || result.payload;
      return { status: true, data: data };
    } else {
      const message = result.message || "Error al registrarse en el evento";
      return { status: false, message };
    }
  } catch (error) {
    console.error("Error en registerToEvent:", error);
    const message = error.message || "Error al registrarse en el evento";
    return { status: false, message };
  }
};
